/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { Save, X, Edit, Loader, Package, CheckCircle } from 'lucide-react';
import { UserStory } from '../../types';
import { AcceptanceCriterion, AcceptanceCriterionStatus, ACCEPTANCE_CRITERION_STATUSES, SPECIAL_RELEASE_VALUES, DEFAULT_RELEASE_VALUE, normalizeAcceptanceCriteria } from '@shared/types/user-stories';
import type { Issue } from '@shared/types/issues';

interface UserStoryDetailsProps {
  story: UserStory;
  isEditing: boolean;
  isUpdating: boolean;
  editedFields: Partial<UserStory>;
  statusOptions: string[];
  releases?: Issue[];
  getStatusColor: (status: string) => string;
  onStartEditing: () => void;
  onCancelEditing: () => void;
  onSaveEdits: () => void;
  onFieldChange: (field: keyof UserStory, value: any) => void;
  onStatusUpdate: (newStatus: string) => void;
  onAcceptanceCriteriaChange: (criteria: AcceptanceCriterion[]) => void;
  onLinksChange: (links: string[]) => void;
}

export function UserStoryDetails({
  story,
  isEditing,
  isUpdating,
  editedFields,
  statusOptions,
  releases = [],
  getStatusColor,
  onStartEditing,
  onCancelEditing,
  onSaveEdits,
  onFieldChange,
  onStatusUpdate,
  onAcceptanceCriteriaChange,
  onLinksChange
}: UserStoryDetailsProps) {
  const handleACTextChange = (index: number, text: string) => {
    const currentAC = normalizeAcceptanceCriteria(editedFields.acceptanceCriteria || story.acceptanceCriteria);
    const newAC = [...currentAC];
    // Explicitly preserve all properties, update text
    const existingCriterion = newAC[index];
    newAC[index] = {
      text: text,
      release: existingCriterion.release,
      status: existingCriterion.status,
    };
    onAcceptanceCriteriaChange(newAC);
  };

  const handleACReleaseChange = (index: number, release: string) => {
    const currentAC = normalizeAcceptanceCriteria(editedFields.acceptanceCriteria || story.acceptanceCriteria);
    const newAC = [...currentAC];
    // Explicitly preserve all properties, set release based on selection
    const existingCriterion = newAC[index];
    newAC[index] = {
      text: existingCriterion.text,
      status: existingCriterion.status,
      // Store undefined if release is empty or the default "Unscheduled" value
      release: (release && release !== DEFAULT_RELEASE_VALUE) ? release : undefined,
    };
    onAcceptanceCriteriaChange(newAC);
  };

  const handleACStatusChange = (index: number, status: AcceptanceCriterionStatus) => {
    const currentAC = normalizeAcceptanceCriteria(editedFields.acceptanceCriteria || story.acceptanceCriteria);
    const newAC = [...currentAC];
    // Explicitly preserve all properties, update status
    const existingCriterion = newAC[index];
    newAC[index] = {
      text: existingCriterion.text,
      release: existingCriterion.release,
      status: status,
    };
    onAcceptanceCriteriaChange(newAC);
  };

  const getACStatusColor = (status: AcceptanceCriterionStatus | undefined): string => {
    switch (status) {
      case 'Accepted': return '#22c55e';
      case 'Ready For Review': return '#6f42c1';
      case 'Scoped': return '#0066cc';
      case 'Required':
      default: return '#6b7280';
    }
  };

  // Build release options: special values first, then actual releases
  const releaseOptions = [
    ...SPECIAL_RELEASE_VALUES,
    ...releases.map(r => r.id.split(':')[0].trim())
  ];

  const handleACAdd = () => {
    const currentAC = normalizeAcceptanceCriteria(editedFields.acceptanceCriteria || story.acceptanceCriteria);
    // New criteria default to "Unscheduled" release and "Required" status
    onAcceptanceCriteriaChange([...currentAC, { text: '', release: undefined, status: 'Required' }]);
  };

  const handleACRemove = (index: number) => {
    const currentAC = normalizeAcceptanceCriteria(editedFields.acceptanceCriteria || story.acceptanceCriteria);
    const newAC = currentAC.filter((_, i) => i !== index);
    onAcceptanceCriteriaChange(newAC);
  };

  return (
    <div style={{
      marginTop: '16px',
      paddingTop: '16px',
      borderTop: '1px solid #e0e0e0'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <h5 style={{ margin: 0, color: '#333', fontSize: '16px' }}>
          User Story Details
        </h5>
        <div style={{ display: 'flex', gap: '8px' }}>
          {isEditing ? (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onSaveEdits();
                }}
                disabled={isUpdating}
                className="btn"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  fontSize: '12px',
                  backgroundColor: '#28a745',
                  borderColor: '#28a745'
                }}
              >
                {isUpdating ? (
                  <>
                    <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} />
                    Saving
                  </>
                ) : (
                  <>
                    <Save size={12} />
                    Save
                  </>
                )}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onCancelEditing();
                }}
                disabled={isUpdating}
                className="btn btn-secondary"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  fontSize: '12px'
                }}
              >
                <X size={12} />
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onStartEditing();
              }}
              className="btn"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                fontSize: '12px',
                backgroundColor: '#0066cc',
                borderColor: '#0066cc'
              }}
            >
              <Edit size={12} />
              Edit
            </button>
          )}
        </div>
      </div>
      
      <div style={{ display: 'grid', gap: '12px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', alignItems: 'start' }}>
          <strong style={{ color: '#666' }}>ID:</strong>
          <span>{story.id}</span>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', alignItems: 'start' }}>
          <strong style={{ color: '#666' }}>Title:</strong>
          {isEditing ? (
            <input
              type="text"
              value={editedFields.title !== undefined ? editedFields.title : story.title}
              onChange={(e) => onFieldChange('title', e.target.value)}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                padding: '6px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          ) : (
            <span>{story.title}</span>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', alignItems: 'start' }}>
          <strong style={{ color: '#666' }}>Status:</strong>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <select
              value={story.status || 'Not Started'}
              onChange={(e) => onStatusUpdate(e.target.value)}
              disabled={isUpdating}
              style={{
                padding: '4px 8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: isUpdating ? '#f5f5f5' : 'white',
                cursor: isUpdating ? 'not-allowed' : 'pointer',
                color: getStatusColor(story.status || 'Not Started'),
                fontWeight: '600'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {statusOptions.map(status => (
                <option key={status} value={status} style={{ color: '#333' }}>
                  {status}
                </option>
              ))}
            </select>
            {isUpdating && (
              <Loader size={14} style={{ animation: 'spin 1s linear infinite', color: '#666' }} />
            )}
          </div>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', alignItems: 'start' }}>
          <strong style={{ color: '#666' }}>Role:</strong>
          {isEditing ? (
            <input
              type="text"
              value={editedFields.role !== undefined ? editedFields.role : story.role}
              onChange={(e) => onFieldChange('role', e.target.value)}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                padding: '6px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          ) : (
            <span>{story.role}</span>
          )}
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', alignItems: 'start' }}>
          <strong style={{ color: '#666' }}>Goal:</strong>
          {isEditing ? (
            <textarea
              value={editedFields.goal !== undefined ? editedFields.goal : story.goal}
              onChange={(e) => onFieldChange('goal', e.target.value)}
              onClick={(e) => e.stopPropagation()}
              rows={3}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
          ) : (
            <span>{story.goal}</span>
          )}
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', alignItems: 'start' }}>
          <strong style={{ color: '#666' }}>Benefit:</strong>
          {isEditing ? (
            <textarea
              value={editedFields.benefit !== undefined ? editedFields.benefit : story.benefit}
              onChange={(e) => onFieldChange('benefit', e.target.value)}
              onClick={(e) => e.stopPropagation()}
              rows={3}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
          ) : (
            <span>{story.benefit}</span>
          )}
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', alignItems: 'start' }}>
          <strong style={{ color: '#666' }}>Acceptance Criteria:</strong>
          {isEditing ? (
            <div>
              {normalizeAcceptanceCriteria(editedFields.acceptanceCriteria || story.acceptanceCriteria).map((criterion, index) => (
                <div key={index} style={{ marginBottom: '12px', padding: '8px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ color: '#666', paddingTop: '6px' }}>{index + 1}.</span>
                    <input
                      type="text"
                      value={criterion.text}
                      onChange={(e) => handleACTextChange(index, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      placeholder="Acceptance criterion text"
                      style={{
                        flex: 1,
                        padding: '6px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleACRemove(index);
                      }}
                      className="btn btn-secondary"
                      style={{ padding: '6px 12px', fontSize: '12px' }}
                    >
                      Remove
                    </button>
                  </div>
                  <div style={{ display: 'flex', gap: '16px', marginLeft: '24px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <Package size={14} style={{ color: '#6f42c1' }} />
                      <select
                        value={criterion.release || DEFAULT_RELEASE_VALUE}
                        onChange={(e) => handleACReleaseChange(index, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          padding: '4px 6px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '13px',
                          backgroundColor: 'white',
                          cursor: 'pointer',
                          minWidth: '140px'
                        }}
                      >
                        {releaseOptions.map(release => (
                          <option key={release} value={release}>{release}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <CheckCircle size={14} style={{ color: getACStatusColor(criterion.status) }} />
                      <select
                        value={criterion.status || 'Required'}
                        onChange={(e) => handleACStatusChange(index, e.target.value as AcceptanceCriterionStatus)}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          padding: '4px 6px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '13px',
                          backgroundColor: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        {ACCEPTANCE_CRITERION_STATUSES.map(status => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              ))}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleACAdd();
                }}
                className="btn"
                style={{ padding: '6px 12px', fontSize: '12px' }}
              >
                Add Criteria
              </button>
            </div>
          ) : story.acceptanceCriteria && story.acceptanceCriteria.length > 0 ? (
            <ol style={{ margin: 0, paddingLeft: '20px' }}>
              {normalizeAcceptanceCriteria(story.acceptanceCriteria).map((criterion, index) => (
                <li key={index} style={{ marginBottom: '6px' }}>
                  <span>{criterion.text}</span>
                  <span
                    style={{
                      marginLeft: '8px',
                      padding: '2px 6px',
                      backgroundColor: getACStatusColor(criterion.status),
                      color: 'white',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '500'
                    }}
                  >
                    {criterion.status || 'Required'}
                  </span>
                  {criterion.release && (
                    <span
                      style={{
                        marginLeft: '8px',
                        padding: '2px 6px',
                        backgroundColor: '#6f42c1',
                        color: 'white',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '500'
                      }}
                    >
                      {criterion.release}
                    </span>
                  )}
                </li>
              ))}
            </ol>
          ) : (
            <span style={{ color: '#999', fontStyle: 'italic' }}>No acceptance criteria</span>
          )}
        </div>
        
        {story.section && (
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', alignItems: 'start' }}>
            <strong style={{ color: '#666' }}>Section:</strong>
            <span>{story.section}</span>
          </div>
        )}
        
        {story.epicId && (
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', alignItems: 'start' }}>
            <strong style={{ color: '#666' }}>Epic ID:</strong>
            <span>{story.epicId}</span>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', alignItems: 'start' }}>
          <strong style={{ color: '#666' }}>Links:</strong>
          {isEditing ? (
            <input
              type="text"
              value={(editedFields.links !== undefined ? editedFields.links : story.links || []).join(', ')}
              onChange={(e) => {
                const value = e.target.value;
                const links = value ? value.split(',').map(l => l.trim()).filter(l => l) : [];
                onLinksChange(links);
              }}
              onClick={(e) => e.stopPropagation()}
              placeholder="e.g., BUG-001, TASK-042, https://docs.example.com"
              style={{
                width: '100%',
                padding: '6px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          ) : story.links && story.links.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {story.links.map((link, index) => (
                <span
                  key={index}
                  style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    backgroundColor: link.startsWith('http') ? '#e3f2fd' : '#f5f5f5',
                    borderRadius: '4px',
                    fontSize: '13px',
                    color: link.startsWith('http') ? '#1565c0' : '#666'
                  }}
                >
                  {link.startsWith('http') ? (
                    <a
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#1565c0', textDecoration: 'none' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {link}
                    </a>
                  ) : (
                    link
                  )}
                </span>
              ))}
            </div>
          ) : (
            <span style={{ color: '#999', fontStyle: 'italic' }}>No links</span>
          )}
        </div>
      </div>
      
      <div style={{ 
        marginTop: '12px', 
        fontSize: '12px', 
        color: '#999',
        textAlign: 'center'
      }}>
        Click anywhere on the user story card to collapse
      </div>
    </div>
  );
}

